import Grid from '@mui/material/Grid';
import CTA, { type FieldlessCTAContent } from '@/components/Elements/CTA';
import LocationDisplay from '@/components/GlobalNavigation/Location/LocationDisplay/LocationDisplay';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import Link from '@/components/Link/Link';
import CartIcon from '@/components/SunrunShop/CartIcon/CartIcon';
import { getTotalQty } from '@/components/SunrunShop/utils/quantity';
import useSunrunShopStore from '@/data-store/zustand/hooks/useSunrunShopStore';
import { trackEvents } from '@/utils/analytics';

import useStyles from './NavBanner.styles';

export interface NavBannerProps {
  promo?: FieldlessCTAContent;
  ctaContent?: FieldlessCTAContent;
  login?: FieldlessCTAContent;
  isOnDark: boolean;
  isHelperNav?: boolean;
  solidDarkBackground?: boolean;
}

const NavBanner = ({
  promo,
  ctaContent,
  login,
  isOnDark,
  isHelperNav = false,
  solidDarkBackground = false,
}: NavBannerProps) => {
  const { classes, cx } = useStyles();
  const { cart } = useSunrunShopStore('cart');
  const qty = getTotalQty(cart);

  const getColorVariants = () => {
    //Default to light background non immersive
    const variants = {
      topBannerColorClass: classes.topBannerLight,
      promoClass: classes.promo,
      textStyles: classes.onLightText,
    };
    if (!isHelperNav) {
      variants.topBannerColorClass = cx({
        [classes.topBannerDark]: isOnDark,
        [classes.topBannerLight]: !isOnDark,
      });
      variants.textStyles = cx({
        [classes.onDarkText]: isOnDark,
        [classes.onLightText]: !isOnDark,
      });
    } else {
      variants.textStyles = classes.onLightText;
      variants.topBannerColorClass = classes.topBannerLight;
    }

    return variants;
  };

  const { topBannerColorClass, textStyles } = getColorVariants();
  const bannerBackgroundClass = solidDarkBackground
    ? classes.topBannerSolidDark
    : topBannerColorClass;

  return (
    <div className={cx(classes.topBannerOuter, bannerBackgroundClass)}>
      <GridContainer fixed>
        <Grid size={12} className={classes.topBanner}>
          <div className={cx(classes.locationContainer, classes.bannerItem)}>
            <LocationDisplay isOnDark={isOnDark && !isHelperNav} />
          </div>
          {promo && (
            <div className={classes.bannerItem}>
              <CTA
                gaEvent={{
                  event: 'engagement',
                  id: `promo-${promo.text}`,
                }}
                content={promo}
                className={cx(classes.promo, textStyles)}
              />
            </div>
          )}
          <div className={classes.bannerItem}>
            <div className={classes.contact}>
              {ctaContent && (
                <Link
                  onClick={() => {
                    trackEvents({ event: 'engagement', id: 'phone' });
                  }}
                  href={ctaContent.url}
                  className={cx(classes.phone, textStyles, {
                    [classes.centerOnMobile]: qty === 0,
                  })}
                >
                  <Icon aria-label='phone icon' name={IconName.CALL} />
                  {ctaContent.text}
                </Link>
              )}
              {login && (
                <CTA
                  testId='global-nav-login'
                  gaEvent={{
                    event: 'engagement',
                    id: 'login',
                  }}
                  content={login}
                  className={cx(classes.login, textStyles)}
                />
              )}
              <CartIcon isOnDark={isOnDark && !isHelperNav} />
            </div>
          </div>
        </Grid>
      </GridContainer>
    </div>
  );
};

export default NavBanner;
